import {
  CalendarOutlined,
  CheckCircleFilled,
  DownOutlined,
  ExclamationCircleFilled,
  ShoppingCartOutlined,
} from '@ant-design/icons';
import { Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { DATE_FORMATS } from '../../../../../../common/constants';
import { formatDate } from '../../../../../../common/utils';
import {
  getUnorderedWeekSlots,
  getWeekProgress,
  getWeekSlots,
} from '../../../../ultraPlanUtils';
import RoadmapSlotTile from './RoadmapSlotTile';

const { Text } = Typography;

export const getWeekElementId = (weekNumber) => `ultra-plan-week-${weekNumber}`;

const RoadmapWeek = ({
  week,
  currentWeekNumber,
  disabled,
  isEditDisabled,
  lockReason,
  onEditSlot,
  onSwapSlot,
  onPlaceOrder,
}) => {
  const isCurrentWeek =
    !!week?.isCurrentWeek ||
    (!!currentWeekNumber && week?.weekNumber === currentWeekNumber);
  const [isCollapsed, setIsCollapsed] = useState(
    () => !!currentWeekNumber && week?.weekNumber < currentWeekNumber,
  );
  const weekSlots = getWeekSlots(week);
  const { emptyCount, isComplete } = getWeekProgress(week);
  const isFullyOrdered = isComplete && !getUnorderedWeekSlots(week)?.length;
  const orderHint = isComplete ? '' : 'Fill every slot to place the order.';
  return (
    <Card
      id={getWeekElementId(week?.weekNumber)}
      size="small"
      className={`roadmap-week ${isCollapsed ? 'is-collapsed' : ''} ${
        isCurrentWeek ? 'is-current' : ''
      }`}
      title={
        <Space size={8} wrap>
          <span className="roadmap-week-title">{`Week ${week?.weekNumber}`}</span>
          {isComplete ? (
            <Text type="success" className="ultra-plan-caption">
              <CheckCircleFilled /> All toys planned
            </Text>
          ) : (
            <Text type="warning" className="ultra-plan-caption">
              <ExclamationCircleFilled />
              {` ${emptyCount} slot${emptyCount > 1 ? 's' : ''} empty`}
            </Text>
          )}
          {week?.expectedDeliveryDate && (
            <Text type="secondary" className="ultra-plan-caption">
              <CalendarOutlined />
              {` Est. delivery ${formatDate(
                week?.expectedDeliveryDate,
                DATE_FORMATS?.REGULAR,
              )}`}
            </Text>
          )}
        </Space>
      }
      extra={
        <Space size={8}>
          {isCurrentWeek && (
            <Tag className="swap-week-pill roadmap-current-week-tag">
              Current week
            </Tag>
          )}
          {onPlaceOrder && !isFullyOrdered && (
            <Tooltip title={lockReason || orderHint}>
              <Button
                size="small"
                icon={<ShoppingCartOutlined />}
                disabled={disabled || isEditDisabled || !!orderHint}
                onClick={() => onPlaceOrder(week)}
              >
                Place Order
              </Button>
            </Tooltip>
          )}
          <Tooltip title={isCollapsed ? 'Show toys' : 'Hide toys'}>
            <Button
              size="small"
              type="text"
              className="roadmap-week-toggle"
              icon={<DownOutlined />}
              aria-expanded={!isCollapsed}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} week ${
                week?.weekNumber
              }`}
              onClick={() => setIsCollapsed((prev) => !prev)}
            />
          </Tooltip>
        </Space>
      }
    >
      <div className="roadmap-week-collapse">
        <div className="roadmap-week-slots">
          {weekSlots?.map(({ key, position, slotConfig, slot }) => (
            <RoadmapSlotTile
              key={key}
              slotConfig={slotConfig}
              toy={slot?.toy}
              slot={slot}
              disabled={disabled || isEditDisabled}
              lockReason={isEditDisabled ? lockReason : null}
              onEdit={() =>
                onEditSlot({
                  weekNumber: week?.weekNumber,
                  position,
                  slotConfig,
                  slot,
                })
              }
              onSwap={
                onSwapSlot
                  ? () =>
                      onSwapSlot({
                        weekNumber: week?.weekNumber,
                        position,
                        slotConfig,
                        slot,
                      })
                  : undefined
              }
            />
          ))}
        </div>
      </div>
    </Card>
  );
};

export default RoadmapWeek;
